import React, { ReactNode } from 'react';
import { css } from '@emotion/css';
import classnames from 'classnames';
import { GRAY100, GRAY1000, GRAY200, INFOGRAPHICS_CHARCOAL20 } from '@/domain/colors';

const styles = {
  card: {
    width: '100%',
    flex: '1 1 0',
    minWidth: '276px',
    display: 'flex',
    gap: '8px',
    padding: '28px 24px',
    backgroundColor: 'transparent',
    alignItems: 'start',
    textAlign: 'left',
    border: 'none',
    boxSizing: 'border-box',
    borderRadius: 'var(--border-radius-600)',
    '& > *': {
      position: 'relative',
      zIndex: 2,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      borderRadius: 'inherit',
      backgroundColor: GRAY100,
      transition: 'background-color 0.2s ease',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      borderRadius: 'inherit',
      border: `1px solid ${INFOGRAPHICS_CHARCOAL20}`,
      pointerEvents: 'none',
    },
    '&:hover::before': {
      backgroundColor: GRAY200,
    },
  },
  selectedCard: {
    '&::after': {
      border: `2px solid ${GRAY1000}`,
    },
  },
  checkmark: {
    margin: 'var(--size-200)',
    position: 'relative',
    minWidth: '22px',
    height: '22px',
    width: '22px',
    border: `1px solid ${GRAY1000}`,
    borderRadius: '50%',
  },
  checkmarkActive: {
    border: `2px solid ${GRAY1000}`,
    '&::after': {
      content: '""',
      top: '3px',
      left: '3px',
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      position: 'absolute',
      background: `${GRAY1000}`,
    },
  },
  cardContent: css`
    display: flex;
    flex-direction: column;
  `,
  cardTitle: {
    margin: 0,
  },
  cardSubtitle: {
    marginBottom: 'var(--size-200)',
  },
  monthlyPrice: {
    margin: 0,
  },
} as const;

interface CardProps {
  id: string;
  selected: boolean;
  title: string;
  subtitle: string;
  priceTitle: ReactNode;
  priceBenefit: ReactNode;
  onClick: (id: string) => void;
}

const ProductSelectionCard: React.FC<CardProps> = ({
  id,
  selected,
  title,
  subtitle,
  priceTitle,
  priceBenefit,
  onClick,
}) => {
  const handleCardClick = () => {
    onClick(id);
  };

  return (
    <button
      onClick={handleCardClick}
      className={classnames('cursor-pointer height-full absolute-reference', css(styles.card), {
        [css(styles.selectedCard)]: selected,
      })}
    >
      <span
        data-testid='checkmark'
        className={classnames(css(styles.checkmark), 'font-heading-small-regular font-black', {
          [css(styles.checkmarkActive)]: selected,
        })}
      />
      <div className={styles.cardContent}>
        <h3 className={`${css(styles.cardTitle)}`}>{title}</h3>
        <p className={`${css(styles.cardSubtitle)} font-body-large-regular`}>{subtitle}</p>
        <div className={`${css(styles.monthlyPrice)} font-heading-xsmall-regular`}>{priceTitle}</div>
        <div className='font-body-small-regular'>{priceBenefit}</div>
      </div>
    </button>
  );
};
export default ProductSelectionCard;
